// DataContext.js
import useAuth from 'app/hooks/useAuth'
import { getRequest } from 'app/utils/request'
import PropTypes from 'prop-types'
import React, {
  useMemo,
  useEffect,
  useReducer,
  useContext,
  useCallback,
  createContext,
} from 'react'

// Initial state for the context
const initialState = {
  dashboardStats: null,
  categories: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  colors: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  brands: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  cars: {
    isLoading: false,
    count: 0,
    rows: [],
    page: 1,
    limit: 20,
    query: '',
    userId: 0,
    refetch: false,
  },
  offers: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  subs: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  ads: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  countries: [],
  cylinderCapacity: [],
  cylindersNumber: [],
  packs: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
  users: {
    isLoading: false,
    refetch: false,
    search: '',
    count: 0,
    rows: [],
    page: 1,
    limit: 20,
  },
  banners: [],
  roles: [],
  faqs: [],
  subjects: [],
  about: null,
  notifications: { isLoading: false, count: 0, rows: [], page: 1, limit: 20 },
}

// Action types

//Contact us subject
const SET_SUBJECTS = 'SET_SUBJECTS'
//Notifications
const SET_NOTIFICATIONS = 'SET_NOTIFICATIONS'
const SET_NOTIFICATIONS_PAGE = 'SET_NOTIFICATIONS_PAGE'
const SET_NOTIFICATIONS_LIMIT = 'SET_NOTIFICATIONS_LIMIT'
const SET_NOTIFICATIONS_LOADING = 'SET_NOTIFICATIONS_LOADING'
//about
const SET_ABOUT = 'SET_ABOUT'
const SET_CATEGORIES = 'SET_CATEGORIES'
const SET_CATEGORIES_PAGE = 'SET_CATEGORIES_PAGE'
const SET_CATEGORIES_LIMIT = 'SET_CATEGORIES_LIMIT'
const SET_CATEGORIES_LOADING = 'SET_CATEGORIES_LOADING'

const SET_PACKS = 'SET_PACKS'
const SET_PACKS_PAGE = 'SET_PACKS_PAGE'
const SET_PACKS_LIMIT = 'SET_PACKS_LIMIT'
const SET_PACKS_LOADING = 'SET_PACKS_LOADING'

const SET_COLORS = 'SET_COLORS'
const SET_COLORS_PAGE = 'SET_COLORS_PAGE'
const SET_COLORS_LIMIT = 'SET_COLORS_LIMIT'
const SET_COLORS_LOADING = 'SET_COLORS_LOADING'

const SET_ADS = 'SET_ADS'
const SET_ADS_PAGE = 'SET_ADS_PAGE'
const SET_ADS_LIMIT = 'SET_ADS_LIMIT'
const SET_ADS_LOADING = 'SET_ADS_LOADING'

const SET_SUBS = 'SET_SUBS'
const SET_SUBS_PAGE = 'SET_SUBS_PAGE'
const SET_SUBS_LIMIT = 'SET_SUBS_LIMIT'
const SET_SUBS_LOADING = 'SET_SUBS_LOADING'

const SET_BRANDS = 'SET_BRANDS'
const SET_BRANDS_PAGE = 'SET_BRANDS_PAGE'
const SET_BRANDS_LIMIT = 'SET_BRANDS_LIMIT'
const SET_BRANDS_LOADING = 'SET_BRANDS_LOADING'

const SET_COUNTRIES = 'SET_COUNTRIES'
const SET_CYLINDER_CAPACITY = 'SET_CYLINDER_CAPACITY'
const SET_CYLINDER_NUMBER = 'SET_CYLINDER_NUMBER'

const SET_BANNERS = 'SET_BANNERS'
const SET_FAQS = 'SET_FAQS'

const SET_USERS = 'SET_USERS'
const SET_USERS_PAGE = 'SET_USERS_PAGE'
const SET_USERS_SEARCH = 'SET_USERS_SEARCH'
const SET_USERS_LIMIT = 'SET_USERS_LIMIT'
const SET_USERS_LOADING = 'SET_USERS_LOADING'

const SET_CARS = 'SET_CARS'
const SET_CARS_PAGE = 'SET_CARS_PAGE'
const SET_CARS_LIMIT = 'SET_CARS_LIMIT'
const SET_CARS_REFETCH = 'SET_CARS_REFETCH'
const SET_CARS_USER = 'SET_CARS_USER'
const SET_CARS_QUERY = 'SET_CARS_QUERY'
const SET_CARS_LOADING = 'SET_CARS_LOADING'

const SET_OFFERS = 'SET_OFFERS'
const SET_OFFERS_PAGE = 'SET_OFFERS_PAGE'
const SET_OFFERS_LIMIT = 'SET_OFFERS_LIMIT'
const SET_OFFERS_LOADING = 'SET_OFFERS_LOADING'

const SET_ROLES = 'SET_ROLES'

const SET_DASHBOARD_STATS = 'SET_DASHBOARD_STATS'

const CLEAR_ALL_STATES = 'CLEAR_ALL_STATES'

// Reducer function to handle state changes
const dataReducer = (state, action) => {
  console.log(action, "action")
  switch (action.type) {
    case SET_DASHBOARD_STATS:
      return {
        ...state,
        dashboardStats: action.payload,
      }
    case SET_CATEGORIES:
      return {
        ...state,
        categories: {
          ...state?.categories,
          count: action?.payload?.count ?? state?.categories?.count ?? 0,
          rows: action?.payload?.rows ?? state?.categories?.rows ?? [],
        },
      }

    case SET_CATEGORIES_PAGE: {
      return {
        ...state,
        categories: { ...state.categories, page: action.payload },
      }
    }

    case SET_CATEGORIES_LIMIT:
      return {
        ...state,
        categories: { ...state.categories, limit: action.payload },
      }

    case SET_CATEGORIES_LOADING:
      return {
        ...state,
        categories: { ...state.categories, isLoading: action.payload },
      }

    case SET_COLORS:
      return {
        ...state,
        colors: {
          ...state?.colors,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_COLORS_PAGE: {
      return {
        ...state,
        colors: { ...state.colors, page: action.payload },
      }
    }

    case SET_COLORS_LIMIT:
      return {
        ...state,
        colors: { ...state.colors, limit: action.payload },
      }

    case SET_COLORS_LOADING:
      return {
        ...state,
        colors: { ...state.colors, isLoading: action.payload },
      }
    case SET_CARS:
      return {
        ...state,
        cars: {
          ...state?.cars,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_CARS_PAGE: {
      return {
        ...state,
        cars: { ...state.cars, page: action.payload },
      }
    }

    case SET_CARS_LIMIT:
      return {
        ...state,
        cars: { ...state.cars, limit: action.payload },
      }

    case SET_CARS_USER: {
      return {
        ...state,
        cars: {
          ...state?.cars,
          userId: action?.payload,
        },
      }
    }
    case SET_CARS_REFETCH:
      return {
        ...state,
        cars: {
          ...state?.cars,
          refetch: action?.payload,
        },
      }
    case SET_CARS_QUERY:
      return {
        ...state,
        cars: {
          ...state?.cars,
          query: action?.payload,
        },
      }
    case SET_CARS_LOADING:
      return {
        ...state,
        cars: { ...state.cars, isLoading: action.payload },
      }

    case SET_OFFERS:
      return {
        ...state,
        offers: {
          ...state?.offers,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_OFFERS_PAGE: {
      return {
        ...state,
        offers: { ...state.offers, page: action.payload },
      }
    }

    case SET_OFFERS_LIMIT:
      return {
        ...state,
        offers: { ...state.offers, limit: action.payload },
      }

    case SET_OFFERS_LOADING:
      return {
        ...state,
        offers: { ...state.offers, isLoading: action.payload },
      }

    case SET_BRANDS:
      return {
        ...state,
        brands: {
          ...state?.brands,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_BRANDS_PAGE: {
      return {
        ...state,
        brands: { ...state.brands, page: action.payload },
      }
    }

    case SET_BRANDS_LIMIT:
      return {
        ...state,
        brands: { ...state.brands, limit: action.payload },
      }

    case SET_BRANDS_LOADING:
      return {
        ...state,
        brands: { ...state.brands, isLoading: action.payload },
      }

    case SET_COUNTRIES:
      return {
        ...state,
        countries: action?.payload || [],
      }

    case SET_CYLINDER_CAPACITY:
      return {
        ...state,
        cylinderCapacity: action?.payload || [],
      }

    case SET_CYLINDER_NUMBER:
      return {
        ...state,
        cylindersNumber: action?.payload || [],
      }
    case SET_USERS:
      return {
        ...state,
        users: {
          ...state?.users,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_USERS_SEARCH: {
      return {
        ...state,
        users: { ...state.users, search: action.payload },
      }
    }
    case SET_USERS_PAGE: {
      return {
        ...state,
        users: { ...state.users, page: action.payload },
      }
    }

    case SET_USERS_LIMIT:
      return {
        ...state,
        users: { ...state.users, limit: action.payload },
      }

    case SET_USERS_LOADING:
      return {
        ...state,
        users: { ...state.users, isLoading: action.payload },
      }

    case SET_ROLES:
      return {
        ...state,
        roles: action.payload,
      }

    case 'SET_BANNERS':
      return { ...state, banners: action.payload }

    case 'SET_FAQS':
      return { ...state, faqs: action.payload }

    case SET_PACKS:
      return {
        ...state,
        packs: {
          ...state?.packs,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_PACKS_PAGE: {
      return {
        ...state,
        packs: { ...state.packs, page: action.payload },
      }
    }

    case SET_PACKS_LIMIT:
      return {
        ...state,
        packs: { ...state.packs, limit: action.payload },
      }

    case SET_PACKS_LOADING:
      return {
        ...state,
        packs: { ...state.packs, isLoading: action.payload },
      }

    case SET_ADS:
      return {
        ...state,
        ads: {
          ...state?.ads,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_ADS_PAGE: {
      return {
        ...state,
        ads: { ...state.ads, page: action.payload },
      }
    }

    case SET_ADS_LIMIT:
      return {
        ...state,
        ads: { ...state.ads, limit: action.payload },
      }

    case SET_ADS_LOADING:
      return {
        ...state,
        ads: { ...state.ads, isLoading: action.payload },
      }

    case SET_SUBS:
      return {
        ...state,
        subs: {
          ...state?.subs,
          count: action?.payload?.count || 0,
          rows: action?.payload?.rows || [],
        },
      }

    case SET_SUBS_PAGE: {
      return {
        ...state,
        subs: { ...state.subs, page: action.payload },
      }
    }

    case SET_SUBS_LIMIT:
      return {
        ...state,
        subs: { ...state.subs, limit: action.payload },
      }

    case SET_SUBS_LOADING:
      return {
        ...state,
        subs: { ...state.subs, isLoading: action.payload },
      }
    case SET_SUBJECTS:
      return {
        ...state,
        subjects: action.payload || [],
      }
    case SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          count: action.payload.count || 0,
          rows: action.payload.rows || [],
        },
      }
    case SET_NOTIFICATIONS_PAGE: {
      return {
        ...state,
        notifications: { ...state.notifications, page: action.payload },
      }
    }
    case SET_NOTIFICATIONS_LIMIT:
      return {
        ...state,
        notifications: { ...state.notifications, limit: action.payload },
      }
    case SET_NOTIFICATIONS_LOADING:
      return {
        ...state,
        notifications: { ...state.notifications, isLoading: action.payload },
      }
    case SET_ABOUT:
      return {
        ...state,
        about: action.payload,
      }
    case CLEAR_ALL_STATES:
      return initialState
    default:
      return state
  }
}

// Create the context
const DataContext = createContext()

// Context provider component
const DataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataReducer, initialState)
  const { user } = useAuth()

  // Actions to set user, dashboard data, users, malls, shops, and categories
  const setDashStats = (stats) =>
    dispatch({ type: SET_DASHBOARD_STATS, payload: stats })
  const setBanners = (banners) =>
    dispatch({ type: SET_BANNERS, payload: banners })

  const setRoles = (roles) => dispatch({ type: SET_ROLES, payload: roles })
  const setFaqs = (faqs) => dispatch({ type: SET_FAQS, payload: faqs })
  const setAbout = (about) => dispatch({ type: SET_ABOUT, payload: about })
  const setSubject = (subs) => dispatch({ type: SET_SUBJECTS, payload: subs })
  //notifications
  const setNotifications = (nots) =>
    dispatch({ type: SET_NOTIFICATIONS, payload: nots })
  const setNotificationsPage = (page) =>
    dispatch({ type: SET_NOTIFICATIONS_PAGE, payload: page })
  const setNotificationsLimit = (limit) =>
    dispatch({ type: SET_NOTIFICATIONS_LIMIT, payload: limit })
  const setNotificationsLoading = (loading) =>
    dispatch({ type: SET_NOTIFICATIONS_LOADING, payload: loading })

  const setUsers = (users) => dispatch({ type: SET_USERS, payload: users })
  const setUsersSearch = (data) =>
    dispatch({ type: SET_USERS_SEARCH, payload: data })
  const setUsersPage = (page) =>
    dispatch({ type: SET_USERS_PAGE, payload: page })
  const setUsersLimit = (limit) =>
    dispatch({ type: SET_USERS_LIMIT, payload: limit })
  const setUsersLoading = (loading) =>
    dispatch({ type: SET_USERS_LOADING, payload: loading })

  const setAds = (ads) => dispatch({ type: SET_ADS, payload: ads })
  const setAdsPage = (page) => dispatch({ type: SET_ADS_PAGE, payload: page })
  const setAdsLimit = (limit) =>
    dispatch({ type: SET_ADS_LIMIT, payload: limit })
  const setAdsLoading = (loading) =>
    dispatch({ type: SET_ADS_LOADING, payload: loading })

  const setSubs = (subs) => dispatch({ type: SET_SUBS, payload: subs })
  const setSubsPage = (page) => dispatch({ type: SET_SUBS_PAGE, payload: page })
  const setSubsLimit = (limit) =>
    dispatch({ type: SET_SUBS_LIMIT, payload: limit })
  const setSubsLoading = (loading) =>
    dispatch({ type: SET_SUBS_LOADING, payload: loading })

  const setColors = (colors) => dispatch({ type: SET_COLORS, payload: colors })
  const setColorsPage = (page) =>
    dispatch({ type: SET_COLORS_PAGE, payload: page })
  const setColorsLimit = (limit) =>
    dispatch({ type: SET_COLORS_LIMIT, payload: limit })
  const setColorsLoading = (loading) =>
    dispatch({ type: SET_COLORS_LOADING, payload: loading })

  const setCategories = (categories) =>
    dispatch({ type: SET_CATEGORIES, payload: categories })
  const setCategoriesPage = (page) =>
    dispatch({ type: SET_CATEGORIES_PAGE, payload: page })
  const setCategoriesLimit = (limit) =>
    dispatch({ type: SET_CATEGORIES_LIMIT, payload: limit })
  const setCategoriesLoading = (loading) =>
    dispatch({ type: SET_CATEGORIES_LOADING, payload: loading })

  const setBrands = (brands) => dispatch({ type: SET_BRANDS, payload: brands })
  const setBrandsPage = (page) =>
    dispatch({ type: SET_BRANDS_PAGE, payload: page })
  const setBrandsLimit = (limit) =>
    dispatch({ type: SET_BRANDS_LIMIT, payload: limit })
  const setBrandsLoading = (loading) =>
    dispatch({ type: SET_BRANDS_LOADING, payload: loading })

  const setCars = (cars) => dispatch({ type: SET_CARS, payload: cars })
  const setCarsPage = (page) => dispatch({ type: SET_CARS_PAGE, payload: page })
  const setCarsLimit = (limit) =>
    dispatch({ type: SET_CARS_LIMIT, payload: limit })
  const setCarsRefetch = (bool) =>
    dispatch({ type: SET_CARS_REFETCH, payload: bool })
  const setCarUser = (u) => dispatch({ type: SET_CARS_USER, payload: u })
  const setCarsQuery = (query) =>
    dispatch({ type: SET_CARS_QUERY, payload: query })
  const setCarsLoading = (loading) =>
    dispatch({ type: SET_CARS_LOADING, payload: loading })

  const setOffers = (offers) => dispatch({ type: SET_OFFERS, payload: offers })
  const setOffersPage = (page) =>
    dispatch({ type: SET_OFFERS_PAGE, payload: page })
  const setOffersLimit = (limit) =>
    dispatch({ type: SET_OFFERS_LIMIT, payload: limit })
  const setOffersLoading = (loading) =>
    dispatch({ type: SET_OFFERS_LOADING, payload: loading })

  const setCountries = (countries) =>
    dispatch({ type: SET_COUNTRIES, payload: countries })

  const setCylinderCapacity = (cp) =>
    dispatch({ type: SET_CYLINDER_CAPACITY, payload: cp })
  const setCylinderNumber = (cpn) =>
    dispatch({ type: SET_CYLINDER_NUMBER, payload: cpn })

  const setPacks = (packs) => dispatch({ type: SET_PACKS, payload: packs })
  const setPacksPage = (page) =>
    dispatch({ type: SET_PACKS_PAGE, payload: page })
  const setPacksLimit = (limit) =>
    dispatch({ type: SET_PACKS_LIMIT, payload: limit })
  const setPacksLoading = (loading) =>
    dispatch({ type: SET_PACKS_LOADING, payload: loading })

  const clearAllStatesAction = () => dispatch({ type: CLEAR_ALL_STATES })

  const fetchDashboardStats = useCallback(async () => {
    const res = await getRequest('/admin/get-statistics')
    setDashStats(res?.data)
  }, [])

  const fetchUsers = useCallback(async () => {
    const offset = (state.users.page - 1) * state.users.limit
    let url = `/auth/users/list?offset=${offset}&limit=${state.users.limit}&sortDirection=DESC`
    setUsersLoading(true)
    if (state.users.search !== '') {
      url += `&search=${state.users.search}`
    }
    const res = await getRequest(url)
    setUsersLoading(false)
    setUsers(res?.data)
  }, [state.users.page, state.users.search, state.users.limit])

  const fetchAds = useCallback(async () => {
    const offset = (state.ads.page - 1) * state.ads.limit
    setAdsLoading(true)
    const res = await getRequest(
      `/advert/admin?offset=${offset}&limit=${state.ads.limit}`,
    )
    setAdsLoading(false)
    setAds(res?.data)
  }, [state.ads.page, state.ads.limit])

  const fetchSubs = useCallback(async () => {
    const offset = (state.subs.page - 1) * state.subs.limit
    setSubsLoading(true)
    const res = await getRequest(
      `/subs/admin?offset=${offset}&limit=${state.subs.limit}`,
    )
    setSubsLoading(false)
    setSubs(res?.data)
  }, [state.subs.page, state.subs.limit])

  const fetchRoles = useCallback(async () => {
    const res = await getRequest(`/role/admin`)
    setRoles(res?.data)
  }, [])

  const fetchCategories = useCallback(async () => {
    const offset = (state.categories.page - 1) * state.categories.limit
    setCategoriesLoading(true)
    const res = await getRequest(
      `/category/admin?offset=${offset}&limit=${state.categories.limit}`,
    )
    setCategoriesLoading(false)
    // Support both paginated { count, rows } and legacy array responses
    if (Array.isArray(res?.data)) {
      setCategories({ count: res.data.length, rows: res.data })
    } else {
      setCategories(res?.data)
    }
  }, [state.categories.page, state.categories.limit])

  const fetchColors = useCallback(async () => {
    const offset = (state.colors.page - 1) * state.colors.limit
    setColorsLoading(true)
    const res = await getRequest(
      `/car-colors/?limit=${state.colors.limit}&offset=${offset}`,
    )
    setColorsLoading(false)
    setColors(res?.data)
  }, [state.colors.page, state.colors.limit])

  const fetchPacks = useCallback(async () => {
    const offset = (state.packs.page - 1) * state.packs.limit
    setPacksLoading(true)
    const res = await getRequest(
      `/packs/admin?offset=${offset}&limit=${state.packs.limit}`,
    )
    setPacksLoading(false)
    setPacks(res?.data)
  }, [state.packs.page, state.packs.limit])

  const fetchBrands = useCallback(async () => {
    const offset = (state.brands.page - 1) * state.brands.limit
    setBrandsLoading(true)
    const res = await getRequest(
      `/brand/admin?offset=${offset}&limit=${state.brands.limit}`,
    )
    setBrandsLoading(false)
    setBrands(res?.data)
  }, [state.brands.page, state.brands.limit])

  const fetchCars = useCallback(async () => {
    const offset = (state.cars.page - 1) * state.cars.limit
    setCarsLoading(true)
    let url = `/cars/?offset=${offset}&limit=${state.cars.limit}&sortDirection=DESC`
    if (state.cars.query !== '') {
      url += `&search=${state.cars.query}`
    }
    if (state.cars.userId !== 0) {
      url += `&userId=${state.cars.userId}`
    }
    const res = await getRequest(url)
      const parsedData = {...res?.data, rows: res?.data?.rows?.map((car) => ({
        ...car,
        name: {
          en: car?.brand?.name?.en,
          ar: car?.brand?.name?.ar,
        },
      }))}
    setCarsLoading(false)
    setCars(parsedData)
  }, [state.cars.page, state.cars.limit, state.cars.userId, state.cars.query])

  const fetchOffers = useCallback(async () => {
    const offset = (state.offers.page - 1) * state.offers.limit
    setOffersLoading(true)
    const res = await getRequest(
      `/offers/?offset=${offset}&limit=${state.offers.limit}`,
    )
    setOffersLoading(false)
    setOffers(res?.data)
  }, [state.offers.page, state.offers.limit])

  const fetchCountries = useCallback(async () => {
    const res = await getRequest(`/cars/meta-data`)
    setCountries(res?.data?.countriesList)
    setCylinderCapacity(res?.data?.engineCapacities)
    setCylinderNumber(res?.data?.cylindersNumbers)
  }, [])

  const fetchSlides = useCallback(async () => {
    const res = await getRequest('/slides/slidesAdmin')
    // console.log(res?.data)
    setBanners(res?.data)
  }, [])

  const fetchFaqs = useCallback(async () => {
    const res = await getRequest('/faqs/admin')
    // console.log(res?.data)
    setFaqs(res?.data)
  }, [])

  const fetchSubjects = useCallback(async () => {
    const res = await getRequest('/contactUs/subjects/admin')
    // console.log(res?.data)
    setSubject(res?.data)
  }, [])

  const fetchNotifications = useCallback(async () => {
    const offset = (state.notifications.page - 1) * state.notifications.limit
    setNotificationsLoading(true)
    const res = await getRequest(
      `/notifications/general?offset=${offset}&limit=${state.notifications.limit}`,
    )
    setNotificationsLoading(false)
    setNotifications(res?.data)
  }, [state.notifications.page, state.notifications.limit])

  const fetchAbout = useCallback(async () => {
    const res = await getRequest('/about/')
    setAbout(res?.data)
  }, [])

  const clearAllStates = useCallback(() => {
    clearAllStatesAction()
  }, [])

  useEffect(() => {
    if (user !== null) {
      fetchUsers()
    }
  }, [user, fetchUsers])

  useEffect(() => {
    if (state.cars.refetch) {
      fetchCars()
      setCarsRefetch(false)
    }
  }, [state.cars.refetch, fetchCars])

  useEffect(() => {
    if (user !== null) {
      fetchCars()
    }
  }, [user, fetchCars])

  useEffect(() => {
    if (user !== null) {
      fetchAds()
    }
  }, [user, fetchAds])

  useEffect(() => {
    if (user !== null) {
      fetchCategories()
    }
  }, [user, fetchCategories])

  useEffect(() => {
    if (user !== null) {
      fetchColors()
    }
  }, [user, fetchColors])

  useEffect(() => {
    if (user !== null) {
      fetchBrands()
    }
  }, [user, fetchBrands])

  useEffect(() => {
    if (user !== null) {
      fetchPacks()
    }
  }, [user, fetchPacks])

  useEffect(() => {
    if (user !== null) {
      fetchSubs()
    }
  }, [user, fetchSubs])

  useEffect(() => {
    if (user !== null) {
      fetchOffers()
    }
  }, [user, fetchOffers])

  useEffect(() => {
    if (user !== null) {
      fetchNotifications()
    }
  }, [user, fetchNotifications])

  useEffect(() => {
    if (user !== null) {
      fetchDashboardStats()
      fetchRoles()
      fetchCountries()
      fetchSlides()
      fetchFaqs()
      fetchSubjects()
      fetchAbout()
    }
  }, [
    fetchDashboardStats,
    user,
    fetchRoles,
    fetchCountries,
    fetchSlides,
    fetchFaqs,
    fetchSubjects,
    fetchAbout,
  ])

  // Memoize the context value to avoid unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      ...state,
      setCategories,
      setCategoriesPage,
      setCategoriesLimit,
      setColors,
      setColorsPage,
      setColorsLimit,
      setPacks,
      setPacksPage,
      setPacksLimit,
      setUsers,
      setUsersSearch,
      setUsersPage,
      setUsersLimit,
      setBrands,
      setBrandsPage,
      setBrandsLimit,
      setCountries,
      setBanners,
      setRoles,
      setAds,
      setAdsPage,
      setAdsLimit,
      setSubs,
      setSubsPage,
      setSubsLimit,
      setAbout,
      setFaqs,
      setCars,
      setCarsPage,
      setCarsLimit,
      setCarsRefetch,
      setCarsQuery,
      setCarUser,
      setOffers,
      setOffersPage,
      setOffersLimit,
      setSubject,
      setNotifications,
      setNotificationsPage,
      setNotificationsLimit,
      clearAllStates,
      fetchCategories,
      fetchAds,
      fetchPacks,
      fetchSlides,
      fetchBrands,
      fetchCountries,
      fetchRoles,
      fetchSubs,
      fetchUsers,
      fetchCars,
      fetchFaqs,
      fetchSubjects,
      fetchNotifications,
    }),
    [state], // Only recompute the value when state changes
  )

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  )
}

// Custom hook to use the context
const useDataContext = () => {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider')
  }
  return context
}

DataProvider.propTypes = {
  children: PropTypes.node,
}

export { DataProvider, useDataContext }
